import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  FileButton,
  Group,
  Image,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import useDashboardAccess from "@/hooks/useDashboardAccess";
import {
  getPortfolio,
  moveItem,
  PortfolioImage,
  savePortfolio,
  StaffPortfolio,
  uploadPortfolioImage,
  validatePortfolioFiles,
} from "@/services/merchantUser/portfolio";

export default function Portfolio({ memberId }: { memberId: number }) {
  const { isManager } = useDashboardAccess();
  const query = useQuery({
    queryKey: ["staffPortfolio", memberId],
    queryFn: () => getPortfolio(memberId),
    enabled: memberId > 0,
    refetchOnWindowFocus: false,
  });
  const [draft, setDraft] = useState<StaffPortfolio>();
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState("");
  const [needsReload, setNeedsReload] = useState(false);
  const [notice, setNotice] = useState("");
  const [serverCount, setServerCount] = useState(0);
  const dragged = useRef<number | null>(null);
  const locked = useRef(false);

  useEffect(() => {
    if (query.data && !dirty) {
      setDraft(query.data);
      setServerCount(query.data.images.length);
    }
  }, [query.data, dirty]);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (dirty || busy) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, busy]);

  const editImages = (images: PortfolioImage[]) => {
    if (!draft || busy || !isManager) return;
    setDraft({ ...draft, images });
    setDirty(true);
    setNotice("");
  };
  const move = (from: number, to: number) =>
    draft && editImages(moveItem(draft.images, from, to));
  const upload = async (files: File[]) => {
    if (!draft || !isManager || locked.current || !files.length || needsReload)
      return;
    const validation = validatePortfolioFiles(files, serverCount);
    if (validation) {
      setError(validation);
      return;
    }
    locked.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      for (const file of files) {
        const image = await uploadPortfolioImage(memberId, file);
        setDraft(
          (previous) =>
            previous && {
              ...previous,
              images: [...previous.images, { ...image, description: "" }],
            },
        );
        setServerCount((count) => count + 1);
        setDirty(true);
      }
    } catch {
      setNeedsReload(true);
      setError(
        "آپلود متوقف شد. تصاویر موفق در لیست باقی مانده‌اند؛ برای اطمینان از وضعیت تصاویر، نمونه‌کار را دوباره دریافت کنید.",
      );
    } finally {
      locked.current = false;
      setBusy(false);
    }
  };
  const save = async () => {
    if (!draft || !isManager || locked.current || needsReload) return;
    locked.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await savePortfolio(draft);
      setServerCount(draft.images.length);
      // Keep the draft protected until the saved portfolio has been fetched.
      const refreshed = await query.refetch();
      if (refreshed.data && !refreshed.isError) {
        setDraft(refreshed.data);
        setDirty(false);
      } else {
        setNeedsReload(true);
        setError(
          "ذخیره انجام شد، اما دریافت مجدد ناموفق بود. پیش از ویرایش دوباره دریافت کنید.",
        );
      }
      setNotice("بیوگرافی و نمونه‌کارها ذخیره شدند.");
    } catch {
      setError(
        "ذخیره انجام نشد. تغییرات شما حفظ شده‌اند؛ دوباره تلاش کنید یا نمونه‌کار را از سرور دریافت کنید.",
      );
    } finally {
      locked.current = false;
      setBusy(false);
    }
  };
  const reload = async () => {
    if (
      locked.current ||
      (dirty &&
        !window.confirm(
          "تغییرات ذخیره‌نشده کنار گذاشته شوند؟ تصاویر آپلودشده باقی می‌مانند.",
        ))
    )
      return;
    setBusy(true);
    const result = await query.refetch();
    if (result.data && !result.isError) {
      setDraft(result.data);
      setServerCount(result.data.images.length);
      setDirty(false);
      setNeedsReload(false);
      setError("");
    }
    setBusy(false);
  };

  if (query.isLoading) return <Loader aria-label="دریافت نمونه‌کار" />;
  if (!draft)
    return (
      <Alert color="red">
        دریافت نمونه‌کار انجام نشد.
        <Button onClick={() => void query.refetch()}>تلاش مجدد</Button>
      </Alert>
    );
  return (
    <Stack>
      {error && (
        <Alert color="red" role="alert">
          {error}
          <Button
            variant="subtle"
            disabled={busy}
            onClick={() => void reload()}
          >
            دریافت دوباره
          </Button>
        </Alert>
      )}
      {notice && (
        <Alert color="green" role="status">
          {notice}
        </Alert>
      )}
      {isManager ? (
        <>
          <Textarea
            label="بیوگرافی آرایشگر"
            maxLength={2000}
            autosize
            minRows={3}
            disabled={busy}
            value={draft.description ?? ""}
            onChange={(event) => {
              setDraft({ ...draft, description: event.currentTarget.value });
              setDirty(true);
              setNotice("");
            }}
            description="معرفی آرایشگر، تخصص‌ها و تجربهٔ کاری او؛ حداکثر ۲۰۰۰ کاراکتر"
          />
          <Text fw={600}>نمونه‌کارها</Text>
          <Alert>
            تصاویر بلافاصله آپلود می‌شوند و حتی بدون ذخیره باقی می‌مانند. حذف،
            ترتیب، توضیح تصاویر و بیوگرافی با دکمهٔ ذخیره اعمال می‌شوند.
          </Alert>
          <Group>
            <FileButton
              multiple
              accept=".jpg,.jpeg,.png,.gif"
              onChange={(files) => void upload(files)}
            >
              {(props) => (
                <Button
                  {...props}
                  disabled={busy || needsReload || serverCount >= 30}
                >
                  افزودن عکس نمونه‌کار
                </Button>
              )}
            </FileButton>
            <Text size="sm">
              {draft.images.length} از ۳۰ تصویر · حداکثر ۱۰ مگابایت برای هر
              تصویر
            </Text>
          </Group>
          <Text size="sm" c="dimmed">
            برای تغییر ترتیب، تصاویر را بکشید یا از دکمه‌های بالا و پایین
            استفاده کنید.
          </Text>
        </>
      ) : (
        draft.description && (
          <Stack gap="xs">
            <Text fw={600}>بیوگرافی آرایشگر</Text>
            <Text style={{ whiteSpace: "pre-wrap" }}>{draft.description}</Text>
          </Stack>
        )
      )}
      {!isManager && <Text fw={600}>نمونه‌کارها</Text>}
      {!draft.images.length && (
        <Text c="dimmed">هنوز تصویری برای این عضو ثبت نشده است.</Text>
      )}
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        {draft.images.map((item, index) => (
          <Card
            key={item.id}
            withBorder
            draggable={isManager && !busy}
            onDragStart={(event) => {
              dragged.current = index;
              event.dataTransfer.setData("text/plain", String(item.id));
            }}
            onDragEnd={() => {
              dragged.current = null;
            }}
            onDragOver={(event) => {
              if (isManager && !busy) event.preventDefault();
            }}
            onDrop={(event) => {
              event.preventDefault();
              if (dragged.current !== null) move(dragged.current, index);
              dragged.current = null;
            }}
          >
            <Image
              src={
                item.image.savedPath
                  ? new URL(
                      item.image.savedPath.replace(/^\//, ""),
                      import.meta.env.VITE_API_BASE_URL,
                    ).href
                  : undefined
              }
              alt={item.description || `نمونه‌کار ${index + 1}`}
              h={220}
              fit="contain"
              draggable={false}
            />
            {isManager ? (
              <Stack mt="sm">
                <Textarea
                  label={`توضیح تصویر ${index + 1}`}
                  maxLength={500}
                  disabled={busy}
                  value={item.description ?? ""}
                  onChange={(event) =>
                    editImages(
                      draft.images.map((image) =>
                        image.id === item.id
                          ? { ...image, description: event.currentTarget.value }
                          : image,
                      ),
                    )
                  }
                />
                <Group>
                  <Button
                    size="xs"
                    variant="default"
                    disabled={busy || index === 0}
                    onClick={() => move(index, index - 1)}
                  >
                    بالا
                  </Button>
                  <Button
                    size="xs"
                    variant="default"
                    disabled={busy || index === draft.images.length - 1}
                    onClick={() => move(index, index + 1)}
                  >
                    پایین
                  </Button>
                  <Button
                    size="xs"
                    color="red"
                    variant="light"
                    disabled={busy}
                    onClick={() =>
                      editImages(
                        draft.images.filter((image) => image.id !== item.id),
                      )
                    }
                  >
                    حذف
                  </Button>
                </Group>
              </Stack>
            ) : (
              item.description && <Text mt="sm">{item.description}</Text>
            )}
          </Card>
        ))}
      </SimpleGrid>
      {isManager && (
        <Group>
          <Button
            loading={busy}
            disabled={!dirty || needsReload}
            onClick={() => void save()}
          >
            ذخیره تغییرات
          </Button>
          <Button
            variant="default"
            disabled={busy || !dirty}
            onClick={() => void reload()}
          >
            بازگرداندن تغییرات
          </Button>
          {dirty && <Text size="sm">تغییرات ذخیره‌نشده دارید.</Text>}
        </Group>
      )}
    </Stack>
  );
}
